import { createContext, useContext } from 'react';

/**
 * Context for interactive preview behavior.
 *
 * When the preview is in "interactive" mode (user viewing, not export),
 * content elements can respond to clicks (scroll editor to section,
 * start inline editing) and show hover highlights.
 *
 * During export (Playwright screenshot), the context is absent or
 * `interactive` is false — no handlers, no extra classes.
 *
 * Phase 3 additions:
 *   editingField  — which field is currently being edited inline (null if none)
 *   onEditChange  — (fieldName, newHtml) => void — sync inline changes to state
 *   onEndEdit     — () => void — end current inline editing session
 *
 * Session 46 additions (Layout Nudge):
 *   scale            — current preview scale factor (needed for drag math)
 *   layoutNudge      — current slide's layoutNudge data ({ content: { dx, dy }, logo: { dx, dy } })
 *   onNudgeEnd       — (groupId, { dx, dy }) => void — commit nudge offset to slide data
 *   nudgeConstraints — template-defined constraints per group ({ content: {...}, logo: {...} })
 *
 * Session 48 additions (Content Element Reorder):
 *   contentOrder     — current slide's content order array
 *   onReorder        — (newContentOrder) => void — commit new element order to slide data
 */
const PreviewInteractionContext = createContext({
  interactive: false,
  onElementClick: null,
  editingField: null,
  onEditChange: null,
  onEndEdit: null,
  scale: 0.35,
  layoutNudge: null,
  onNudgeEnd: null,
  nudgeConstraints: null,
  contentOrder: null,
  onReorder: null,
});

export function PreviewInteractionProvider({
  interactive,
  onElementClick,
  editingField,
  onEditChange,
  onEndEdit,
  scale,
  layoutNudge,
  onNudgeEnd,
  nudgeConstraints,
  contentOrder,
  onReorder,
  children,
}) {
  return (
    <PreviewInteractionContext.Provider
      value={{
        interactive,
        onElementClick,
        editingField,
        onEditChange,
        onEndEdit,
        scale: scale || 0.35,
        layoutNudge: layoutNudge || null,
        onNudgeEnd: onNudgeEnd || null,
        nudgeConstraints: nudgeConstraints || null,
        contentOrder: contentOrder || null,
        onReorder: onReorder || null,
      }}
    >
      {children}
    </PreviewInteractionContext.Provider>
  );
}

/**
 * Hook to access preview interaction handlers.
 *
 * Returns { interactive, onElementClick, editingField, onEditChange, onEndEdit, scale, layoutNudge, onNudgeEnd, contentOrder, onReorder }.
 * If outside a provider, interactive=false and all callbacks null (safe for export).
 */
export function usePreviewInteraction() {
  return useContext(PreviewInteractionContext);
}
